import { Modal, type ModalProps } from '@app/components'
import { showMessageNotification, showMessagesNotifications, useUploadDocuments } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarSchoolYear, type SugarUploadType } from '@app/types'
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  LoadingOverlay,
  RingProgress,
  Select,
  Stack,
  Text,
  Textarea,
  useMantineTheme
} from '@mantine/core'
import { Dropzone, type FileWithPath } from '@mantine/dropzone'
import { useForceUpdate } from '@mantine/hooks'
import { IconUpload, IconX, IconFile } from '@tabler/icons-react'
import { concat, find, isEmpty, map, omitBy, some, without } from 'lodash'
import { type FC, useCallback, useState } from 'react'

export interface UploadDocumentModalProps extends ModalProps {
  uploadType: SugarUploadType | null | undefined
}

export const UploadDocumentModal: FC<UploadDocumentModalProps> = ({ onClose, uploadType, ...props }) => {
  const forceUpdate = useForceUpdate()
  const { mutateAsync: uploadDocuments, isPending: isUploadingDocumens, progress } = useUploadDocuments()
  const sugar = useSugarContext()
  const [files, setFiles] = useState<FileWithPath[]>([])
  const [notes, setNotes] = useState('')
  const [postData, setPostData] = useState<Record<string, string | null | undefined>>(() => ({}))
  const theme = useMantineTheme()

  const handleClose = useCallback(() => {
    setFiles([])
    setNotes('')
    setPostData({})
    onClose()
  }, [onClose])

  return uploadType ? (
    <Modal
      onClose={handleClose}
      title={uploadType.displayName || 'Upload Documents'}
      {...props}
    >
      <LoadingOverlay
        visible={isUploadingDocumens}
        loader={
          <RingProgress
            sections={[{ value: progress, color: 'navy' }]}
            label={
              <Text
                align='center'
                color='blue'
                fw='bold'
              >
                {progress}%
              </Text>
            }
          />
        }
      />
      <Stack spacing='xs'>
        {uploadType.helperText && (
          <Text
            dangerouslySetInnerHTML={{ __html: uploadType.helperText }}
            size='sm'
            style={{
              textAlign: 'justify'
            }}
          />
        )}
        {uploadType.templateId && (
          <Anchor
            href={`/api/templates/${uploadType.templateId}`}
            size='sm'
            target='_self'
          >
            Download Template
          </Anchor>
        )}
        <Dropzone
          accept={uploadType.mimeTypes}
          disabled={!uploadType.multiple && files.length > 0}
          maxSize={30_000_000}
          multiple={uploadType.multiple ?? false}
          onDrop={(dropped) => setFiles((prev) => concat(prev, dropped))}
          onReject={(fileRejections) => {
            if (fileRejections[0].errors[0].code === 'file-too-large') {
              showMessageNotification({
                message: 'Max file size is 30mb!',
                type: 'warning'
              })
            }
          }}
        >
          <Group
            position='center'
            spacing='xl'
            style={{ minHeight: 200, pointerEvents: 'none' }}
          >
            <Dropzone.Accept>
              <IconUpload
                size='3.2rem'
                stroke={1.5}
                color={theme.colors[theme.primaryColor][6]}
              />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX
                size='3.2rem'
                stroke={1.5}
                color={theme.colors.red[6]}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFile
                size='3.2rem'
                stroke={1.5}
              />
            </Dropzone.Idle>

            {uploadType.multiple || !files.length ? (
              <Text
                size='xl'
                inline
              >
                Drag files here or click to select files
              </Text>
            ) : !uploadType.multiple ? (
              <Text
                size='xl'
                inline
              >
                Maximum number of files selected
              </Text>
            ) : null}
          </Group>
        </Dropzone>
        {map(files, (file, idx) => (
          <Group
            key={`file-${idx}`}
            noWrap
            position='apart'
          >
            <Text
              fw='bold'
              size='sm'
            >
              {file.name}
            </Text>
            <ActionIcon onClick={() => setFiles((prev) => without(prev, prev[idx]))}>
              <IconX
                color='red'
                size={18}
              />
            </ActionIcon>
          </Group>
        ))}
        {map(uploadType.options, (option) => {
          const data = sugar?.options?.[option.optionsList]

          if (!data) {
            return null
          }

          if (option.optionsList === 'schoolYears' && !postData[option.formField]) {
            const defaultOption = find(data as SugarSchoolYear[], 'default')

            if (defaultOption) {
              setPostData((prev) => {
                prev[option.formField] = defaultOption.value

                return prev
              })
              forceUpdate()
            }
          }

          return (
            <Select
              data={map(data, ({ label, value }) => ({ label, value }))}
              key={option.formField}
              label={option.displayName}
              onChange={(value) => {
                setPostData((prev) => {
                  prev[option.formField] = value

                  return prev
                })
                forceUpdate()
              }}
              required={option.required}
              value={postData[option.formField]}
            />
          )
        })}
        {uploadType.allowNotes && (
          <Textarea
            label='Notes'
            rows={4}
            onChange={(e) => setNotes(e.target.value)}
            value={notes}
          />
        )}
        <Group
          mt='lg'
          position='right'
        >
          <Button
            disabled={
              files.length === 0 ||
              (uploadType.options?.length > 0 &&
                some(uploadType?.options, (option) => option.required && !postData[option.formField]))
            }
            leftIcon={<IconUpload size={18} />}
            onClick={async () => {
              if (uploadType) {
                const { success, messages } = await uploadDocuments({
                  files,
                  uploadType: uploadType.type,
                  ...(uploadType.allowNotes ? { notes } : {}),
                  ...omitBy(postData, isEmpty)
                })

                showMessagesNotifications(messages)

                if (success) {
                  handleClose()
                }
              }
            }}
          >
            Upload Document{files.length > 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>
    </Modal>
  ) : null
}
